import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth } from "../config/firebase";
import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://api.arconest.cloud",
  headers: {
    "Content-Type": "application/json",
  },
});

const AuthContext = createContext();

// Constants for localStorage keys
const STORAGE_KEYS = {
  USER_DATA: "user_data",
  AUTH_METHOD: "auth_method",
  TOKEN: "auth_token",
  OTP_USER: "otp_user_data",
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // State for Firebase user
  const [currentUser, setCurrentUser] = useState(null);
  // State for MongoDB user data
  const [userData, setUserData] = useState(() => {
    // Initialize from localStorage if available
    try {
      const savedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return savedUserData ? JSON.parse(savedUserData) : null;
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
      return null;
    }
  });
  // Loading states
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  // Token for API requests
  const [token, setToken] = useState(() => {
    // Initialize from localStorage if available
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  });
  // Track auth method
  const [authMethod, setAuthMethod] = useState(() => {
    // Initialize from localStorage if available
    return localStorage.getItem(STORAGE_KEYS.AUTH_METHOD);
  });

  // Set Firebase persistence to LOCAL (this keeps user logged in across page refreshes)
  useEffect(() => {
    const setAuthPersistence = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
      } catch (error) {
        console.error("Error setting auth persistence:", error);
      }
    };

    setAuthPersistence();
  }, []);

  // Synchronous restore for OTP users before Firebase onAuth runs.
  // This reduces flashes/redirects on page reload for OTP-authenticated users.
  useEffect(() => {
    try {
      const savedAuthMethod = localStorage.getItem(STORAGE_KEYS.AUTH_METHOD);
      const savedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      const savedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);

      if (savedUserData) {
        const parsed = JSON.parse(savedUserData);
        // Prefer explicit auth method, but if missing assume OTP when a phone exists
        if (savedAuthMethod === "otp" || (!savedAuthMethod && parsed?.phone)) {
          console.log("Restoring OTP session from localStorage (fallback)");
          setUserData(parsed);
          setAuthMethod("otp");
          if (savedToken) setToken(savedToken);
          setLoading(false);
        }
      }
    } catch (err) {
      console.error("Error restoring OTP session from localStorage:", err);
    }
    // run once on mount
  }, []);

  // Save auth data to localStorage whenever it changes
  useEffect(() => {
    if (userData) {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    }
  }, [userData]);

  useEffect(() => {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    } else {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
    }
  }, [token]);

  useEffect(() => {
    if (authMethod) {
      localStorage.setItem(STORAGE_KEYS.AUTH_METHOD, authMethod);
    } else {
      localStorage.removeItem(STORAGE_KEYS.AUTH_METHOD);
    }
  }, [authMethod]);

  // Initialize auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Firebase auth state changed:", user?.email);

      if (user) {
        // Firebase user is logged in
        setCurrentUser(user);
        setAuthMethod("google");

        try {
          // Get Firebase token
          const firebaseToken = await user.getIdToken();
          setToken(firebaseToken);

          // Check if user exists in database
          const existingUser = await checkUserExists(user.email, null);

          if (existingUser) {
            setUserData(existingUser);
            console.log("User data loaded from Firebase persistence");
          } else {
            console.log("No user data found in database for Firebase user");
            // Keep the user logged in with Firebase but show as not registered
            setUserData(null);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          // Even if there's an error, keep Firebase auth state
          setUserData(null);
        }
      } else {
        // No Firebase user - check for OTP user
        const savedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
        const savedAuthMethod = localStorage.getItem(STORAGE_KEYS.AUTH_METHOD);

        // Accept explicit otp flag, or fallback to OTP when user_data contains a phone
        let restoredAsOtp = false;
        if (savedAuthMethod === "otp") {
          restoredAsOtp = true;
        } else if (!savedAuthMethod && savedUserData) {
          try {
            const parsed = JSON.parse(savedUserData);
            if (parsed?.phone) restoredAsOtp = true;
          } catch (e) {
            console.error(
              "Error parsing saved USER_DATA during onAuthStateChanged:",
              e
            );
          }
        }

        if (restoredAsOtp) {
          // Prefer USER_DATA (normal storage), fall back to OTP_USER wrapper
          if (savedUserData) {
            try {
              const otpUser = JSON.parse(savedUserData);
              setUserData(otpUser);
              setAuthMethod("otp");
              console.log(
                "OTP user restored from USER_DATA (onAuthStateChanged)"
              );
            } catch (error) {
              console.error("Error parsing USER_DATA for OTP user:", error);
              clearAllAuthData();
            }
          } else {
            // Try otp-specific storage shape: { user: {...}, timestamp }
            const savedOtpWrapper = localStorage.getItem(STORAGE_KEYS.OTP_USER);
            if (savedOtpWrapper) {
              try {
                const parsed = JSON.parse(savedOtpWrapper);
                const otpUser = parsed?.user || parsed;
                setUserData(otpUser);
                setAuthMethod("otp");
                console.log(
                  "OTP user restored from OTP_USER (onAuthStateChanged)"
                );
              } catch (error) {
                console.error("Error parsing OTP_USER:", error);
                clearAllAuthData();
              }
            } else {
              clearAllAuthData();
            }
          }
        } else {
          // No saved session
          clearAllAuthData();
        }
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Helper function to clear all auth data
  const clearAllAuthData = useCallback(() => {
    setCurrentUser(null);
    setUserData(null);
    setToken(null);
    setAuthMethod(null);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.AUTH_METHOD);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.OTP_USER);
  }, []);

  // Check if user exists in MongoDB
  const checkUserExists = useCallback(async (email, phone) => {
    try {
      const params = {};
      if (email) params.email = email;
      if (phone) params.phone = phone;

      const response = await api.get("/api/users/fetch", { params });

      if (response.data.users && response.data.users.length > 0) {
        return response.data.users[0];
      }
      return null;
    } catch (error) {
      console.error("Error checking user:", error);
      if (
        error.response?.status === 404 ||
        error.response?.data?.users?.length === 0
      ) {
        return null;
      }
      throw error;
    }
  }, []);

  // Google Sign In
  const signInWithGoogle = async (role = null) => {
    try {
      setAuthLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Get Firebase token
      const firebaseToken = await user.getIdToken();
      setToken(firebaseToken);
      setAuthMethod("google");

      // Check if user exists in database
      const existingUser = await checkUserExists(user.email, null);

      if (existingUser) {
        // User exists - set user data
        setUserData(existingUser);
        return {
          success: true,
          exists: true,
          user: existingUser,
          firebaseUser: user,
        };
      } else {
        // User doesn't exist
        return {
          success: true,
          exists: false,
          firebaseUser: user,
          role: role,
        };
      }
    } catch (error) {
      console.error("Google Sign In Error:", error);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  // OTP Login Flow
  const sendOtp = async (phoneNumber) => {
    try {
      setAuthLoading(true);

      // Format phone number
      const formattedPhone = phoneNumber.startsWith("+")
        ? phoneNumber
        : `+91${phoneNumber}`;

      const response = await api.post("/api/auth/send-otp", {
        phone: formattedPhone,
      });

      return {
        success: true,
        message: "OTP sent successfully",
        phone: formattedPhone,
      };
    } catch (error) {
      console.error("Send OTP Error:", error);
      throw new Error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setAuthLoading(false);
    }
  };

  const verifyOtp = async (phoneNumber, otp) => {
    try {
      setAuthLoading(true);

      // Format phone number
      const formattedPhone = phoneNumber.startsWith("+")
        ? phoneNumber
        : `+91${phoneNumber}`;

      // Verify OTP with backend
      const verifyResponse = await api.post("/api/auth/verify-otp", {
        phone: formattedPhone,
        otp: otp,
      });

      if (!verifyResponse.data.success) {
        throw new Error(verifyResponse.data.message || "Invalid OTP");
      }

      // OTP verified successfully
      // Now check if user exists in database
      const userResponse = await api.get("/api/users/fetch", {
        params: { phone: formattedPhone },
      });

      if (userResponse.data.users?.[0]) {
        // User exists - complete login
        const userData = userResponse.data.users[0];
        setUserData(userData);
        setAuthMethod("otp");
        setCurrentUser(null); // No Firebase user for OTP login

        // Create a token for API requests
        const apiToken = `otp_${userData._id}_${Date.now()}`;
        setToken(apiToken);
        // Persist OTP user data so session survives page refresh
        try {
          localStorage.setItem(
            STORAGE_KEYS.OTP_USER,
            JSON.stringify({ user: userData, timestamp: Date.now() })
          );
          localStorage.setItem(
            STORAGE_KEYS.USER_DATA,
            JSON.stringify(userData)
          );
          localStorage.setItem(STORAGE_KEYS.AUTH_METHOD, "otp");
        } catch (e) {
          console.error("Failed to persist OTP user on verifyOtp:", e);
        }

        return {
          success: true,
          exists: true,
          user: userData,
          message: "Login successful",
        };
      } else {
        // User doesn't exist - need registration
        return {
          success: true,
          exists: false,
          phone: formattedPhone,
          message: "Please complete registration",
        };
      }
    } catch (error) {
      console.error("Verify OTP Error:", error);
      throw new Error(error.response?.data?.message || "Invalid OTP");
    } finally {
      setAuthLoading(false);
    }
  };

  // OTP Login - sets user data directly (for backward compatibility)
  const otpLogin = async (userData) => {
    try {
      setAuthLoading(true);

      if (!userData || !userData._id) {
        throw new Error("Invalid user data");
      }

      // Set user data in context
      setUserData(userData);
      setAuthMethod("otp");

      // Generate a simple token
      const authToken = `otp_token_${Date.now()}`;
      setToken(authToken);

      // Save OTP user separately for easier restoration
      // persist both wrapper and USER_DATA to ensure restoration
      localStorage.setItem(
        STORAGE_KEYS.OTP_USER,
        JSON.stringify({
          user: userData,
          timestamp: Date.now(),
        })
      );
      try {
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
        localStorage.setItem(STORAGE_KEYS.AUTH_METHOD, "otp");
      } catch (e) {
        console.error("Failed to persist OTP user to USER_DATA:", e);
      }

      return {
        success: true,
        user: userData,
        token: authToken,
      };
    } catch (error) {
      console.error("OTP Login Error:", error);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  // Test Login Flow
  const signInWithTestCredentials = async (email, password) => {
    try {
      setAuthLoading(true);
      const response = await api.post("/api/auth/test-login", {
        email,
        password,
      });

      if (response.data.success) {
        const userData = response.data.user;
        const apiToken = response.data.token;

        setUserData(userData);
        setAuthMethod("otp");
        setCurrentUser(null);
        setToken(apiToken);

        // Persist session to localStorage
        try {
          localStorage.setItem(
            STORAGE_KEYS.OTP_USER,
            JSON.stringify({ user: userData, timestamp: Date.now() })
          );
          localStorage.setItem(
            STORAGE_KEYS.USER_DATA,
            JSON.stringify(userData)
          );
          localStorage.setItem(STORAGE_KEYS.AUTH_METHOD, "otp");
          localStorage.setItem(STORAGE_KEYS.TOKEN, apiToken);
        } catch (e) {
          console.error("Failed to persist test user on signInWithTestCredentials:", e);
        }

        return {
          success: true,
          user: userData,
          message: "Login successful",
        };
      } else {
        throw new Error(response.data.message || "Test login failed");
      }
    } catch (error) {
      console.error("Test Login Error:", error);
      throw new Error(error.response?.data?.message || error.message || "Test login failed");
    } finally {
      setAuthLoading(false);
    }
  };

  // Register new user
  const registerUser = async (userData) => {
    try {
      setAuthLoading(true);
      const response = await api.post("/api/users/register", userData);

      console.log("Registration response:", response.data);

      if (response.status >= 200 && response.status < 300) {
        const result = response.data;

        if (result.message && result.message.includes("Await admin approval")) {
          // Registration successful but needs admin approval
          return {
            success: true,
            pending: true,
            message: result.message,
            user: result.user || result.data,
          };
        } else if (result.success && result.user) {
          // User registered and is active
          const newUser = result.user;
          setUserData(newUser);

          // Determine auth method based on registration data
          if (userData.email && !userData.phone) {
            setAuthMethod("google");
          } else if (userData.phone) {
            setAuthMethod("otp");
            const apiToken = `reg_token_${newUser._id}_${Date.now()}`;
            setToken(apiToken);

            // Save OTP user
            localStorage.setItem(
              STORAGE_KEYS.OTP_USER,
              JSON.stringify({
                user: newUser,
                timestamp: Date.now(),
              })
            );
            try {
              localStorage.setItem(
                STORAGE_KEYS.USER_DATA,
                JSON.stringify(newUser)
              );
              localStorage.setItem(STORAGE_KEYS.AUTH_METHOD, "otp");
            } catch (e) {
              console.error("Failed to persist OTP user on registration:", e);
            }

            return {
              success: true,
              pending: false,
              user: newUser,
              token: apiToken,
              message: result.message || "Registration successful",
            };
          }

          return {
            success: true,
            pending: false,
            user: newUser,
            token: null,
            message: result.message || "Registration successful",
          };
        } else {
          throw new Error(result.message || "Registration failed");
        }
      } else {
        throw new Error(response.data?.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);

      if (
        error.response &&
        error.response.status >= 200 &&
        error.response.status < 300
      ) {
        const result = error.response.data;

        if (result.message && result.message.includes("Await admin approval")) {
          return {
            success: true,
            pending: true,
            message: result.message,
            user: result.user || result.data,
          };
        }

        return result;
      }

      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  // Update user data
  const updateUserData = useCallback((data) => {
    setUserData((prev) => {
      const updated = { ...prev, ...data };
      // Update localStorage as well
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Logout - clear all auth data
  const logout = async () => {
    try {
      // Sign out from Firebase if logged in with Google
      if (authMethod === "google" && currentUser) {
        await signOut(auth);
      }

      // Clear all state and localStorage
      clearAllAuthData();
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear local state even if Firebase logout fails
      clearAllAuthData();
    }
  };

  // Refresh user data from API
  const refreshUserData = useCallback(async () => {
    if (!userData?._id) return;

    try {
      let endpoint;
      if (userData.email) {
        endpoint = `/api/users/fetch?email=${userData.email}`;
      } else if (userData.phone) {
        endpoint = `/api/users/fetch?phone=${userData.phone}`;
      } else {
        return;
      }

      const response = await api.get(endpoint);
      if (response.data.users && response.data.users.length > 0) {
        const updatedUser = response.data.users[0];
        setUserData(updatedUser);
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  }, [userData]);

  // Set auth token (for API requests)
  const setAuthToken = useCallback((newToken) => {
    setToken(newToken);
  }, []);

  // Set up axios interceptor for token
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
    };
  }, [token]);

  const value = {
    // State
    currentUser,
    userData,
    loading,
    authLoading,
    token,
    authMethod,

    // Computed properties
    isAuthenticated: !!userData,
    isGoogleUser: authMethod === "google",
    isOtpUser: authMethod === "otp",

    // Actions
    signInWithGoogle,
    sendOtp,
    verifyOtp,
    otpLogin,
    registerUser,
    logout,
    signInWithTestCredentials,
    checkUserExists,
    updateUserData,
    refreshUserData,
    setAuthToken,
    clearAllAuthData,

    // Helper functions
    hasRole: (role) => userData?.role === role,
    isAdmin: () => userData?.role === "ADMIN",
    isEngineer: () => userData?.role === "ENGINEER",
    isClient: () => userData?.role === "CLIENT",
    isSuperAdmin: () => userData?.role === "SUPERADMIN",
    canAccessTechnicalFlow: () => ["ADMIN", "SUPERADMIN", "ENGINEER"].includes(userData?.role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
